import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import {
  CargoCompanyFormModal,
  CargoCompanyTable,
  CargoCompanyDetailModal,
} from "../../components/backend/cargocompany";
import {
  useCargoCompanies,
  useCargoCompanyDetail,
  useCargoCompanyMutations,
} from "../../hooks/useCargoCompany";
import {
  CargoCompany,
  CargoCompanyDetail,
  CreateCargoCompanyRequest,
  UpdateCargoCompanyRequest,
  CargoCompanyFilters,
} from "../../types/cargoCompany";
import { formatApiError } from "../../utils/formatApiError";
import AdminPage from "./AdminPage";

type ModalMode = "create" | "edit" | "view" | null;

const CargoCompaniesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<CargoCompanyFilters>({
    activeOnly: false,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: ModalMode;
    company?: CargoCompany | CargoCompanyDetail;
  }>({
    open: false,
    mode: null,
  });

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    company?: CargoCompany;
  }>({
    open: false,
  });

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  // Data fetching
  const {
    data: companiesData,
    isLoading,
    error,
    refetch,
  } = useCargoCompanies(filters);

  const {
    data: detailData,
    isLoading: isLoadingDetail,
    refetch: refetchDetail,
  } = useCargoCompanyDetail(
    modalState.company?.id || "",
    modalState.mode === "view" && modalState.open,
  );

  // Mutations
  const {
    create,
    update,
    delete: deleteMutation,
  } = useCargoCompanyMutations();

  const companies = companiesData?.data || [];

  // Filter companies by search term
  const filteredCompanies = companies.filter((company) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      company.name.toLowerCase().includes(searchLower) ||
      company.code.toLowerCase().includes(searchLower) ||
      company.contactEmail.toLowerCase().includes(searchLower)
    );
  });

  const handleCreate = () => {
    setModalState({ open: true, mode: "create" });
  };

  const handleView = (company: CargoCompany) => {
    setModalState({ open: true, mode: "view", company });
  };

  const handleEdit = (company: CargoCompany | CargoCompanyDetail) => {
    setModalState({ open: true, mode: "edit", company });
  };

  const handleDelete = (company: CargoCompany) => {
    setDeleteDialog({ open: true, company });
  };

  const handleManageUsers = (company: CargoCompany) => {
    navigate(`/admin/cargo-companies/${company.id}/users`);
  };

  const confirmDelete = async () => {
    if (!deleteDialog.company) return;

    try {
      await deleteMutation.mutateAsync(deleteDialog.company.id);
      setNotification({
        open: true,
        message: t("admin.cargoCompanies.deleteSuccess"),
        severity: "success",
      });
      setDeleteDialog({ open: false });
      refetch();
    } catch (error) {
      setNotification({
        open: true,
        message: formatApiError(error),
        severity: "error",
      });
    }
  };

  const handleFormSubmit = async (
    data: CreateCargoCompanyRequest | UpdateCargoCompanyRequest,
  ) => {
    try {
      if (modalState.mode === "create") {
        await create.mutateAsync(data as CreateCargoCompanyRequest);
        setNotification({
          open: true,
          message: t("admin.cargoCompanies.createSuccess"),
          severity: "success",
        });
      } else if (modalState.mode === "edit" && modalState.company) {
        await update.mutateAsync({
          id: modalState.company.id,
          data: data as UpdateCargoCompanyRequest,
        });
        setNotification({
          open: true,
          message: t("admin.cargoCompanies.updateSuccess"),
          severity: "success",
        });
      }
      setModalState({ open: false, mode: null });
      refetch();
    } catch (error) {
      // Error will be handled by the form modal
      throw error;
    }
  };

  const handleCloseModal = () => {
    setModalState({ open: false, mode: null });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const isLoadingMutation =
    create.isPending || update.isPending || deleteMutation.isPending;

  return (
    <AdminPage>
      <Box>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4">{t("admin.cargoCompanies.title")}</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              {t("admin.cargoCompanies.refresh")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              {t("admin.cargoCompanies.createCompany")}
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder={t("admin.cargoCompanies.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filters.activeOnly || false}
                  onChange={(e) =>
                    setFilters({ ...filters, activeOnly: e.target.checked })
                  }
                />
              }
              label={t("admin.cargoCompanies.activeOnly")}
            />
          </Box>
        </Paper>

        {/* Table */}
        <CargoCompanyTable
          companies={filteredCompanies}
          isLoading={isLoading}
          error={error}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onManageUsers={handleManageUsers}
          onRowClick={handleManageUsers}
        />

        {/* Create/Edit Modal */}
        {(modalState.mode === "create" || modalState.mode === "edit") && (
          <CargoCompanyFormModal
            open={modalState.open}
            mode={modalState.mode}
            company={modalState.company as CargoCompany}
            onSubmit={handleFormSubmit}
            onClose={handleCloseModal}
            isLoading={isLoadingMutation}
            error={
              modalState.mode === "create"
                ? create.error
                : update.error
            }
          />
        )}

        {/* View Detail Modal */}
        {modalState.mode === "view" && (
          <CargoCompanyDetailModal
            open={modalState.open}
            company={
              detailData?.data ||
              (modalState.company as CargoCompanyDetail) ||
              null
            }
            isLoading={isLoadingDetail}
            onClose={handleCloseModal}
            onEdit={handleEdit}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false })}
        >
          <DialogTitle>{t("admin.cargoCompanies.deleteTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("admin.cargoCompanies.deleteConfirmation", { name: deleteDialog.company?.name })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialog({ open: false })}
              disabled={deleteMutation.isPending}
            >
              {t("admin.cargoCompanies.cancel")}
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
              startIcon={
                deleteMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : null
              }
            >
              {deleteMutation.isPending ? t("admin.cargoCompanies.deleting") : t("admin.cargoCompanies.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default CargoCompaniesPage;

